import { addBusinessDays } from 'date-fns'

import {
  BarrierLegParams,
  PrimaryTerminalLegParams,
  ScheduleGenerator,
  ScheduleLeg,
  Strike,
} from '@/pages/instruments/new-instrument/schedule-builders/types'

export type SupertrackerOptions = {
  strikes: Strike[]
  currency_id: number
  initial_observation_date: Date
  maturity_date: Date
  settlement_cycle: number
  participation_rate: number
  knockdown_input_level: number | null
}

const buildPrimaryLeg = (params: PrimaryTerminalLegParams<SupertrackerOptions>): ScheduleLeg => {
  const {
    currency_id,
    initial_observation_date,
    maturity_date,
    participation_rate,
    settlement_cycle,
    strike,
  } = params

  return {
    leg_id: 1,
    option_type: 'Vanilla',
    contract_type: 'Call',
    region_type: 'European',
    currency_id,
    initial_observation_date,
    strike: strike.value,
    strike_percent: 100,
    instrument_id: strike.underlying_id,
    pay_date: addBusinessDays(maturity_date, settlement_cycle),
    start_observation_date: maturity_date,
    end_observation_date: maturity_date,
    participation_rate: participation_rate / 100,
  }
}

const buildBarrierLeg = (params: BarrierLegParams<SupertrackerOptions>): ScheduleLeg => {
  const { currency_id, final_leg, initial_observation_date, knockdown_input_level, strike } = params

  return {
    leg_id: 2,
    option_type: 'Barrier Vanilla',
    barrier_type: 'Down-and-In',
    contract_type: 'Put',
    region_type: 'European',
    currency_id,
    initial_observation_date,
    participation_rate: -1,
    strike: strike.value,
    strike_percent: strike.percent,
    pay_date: final_leg.pay_date,
    start_observation_date: final_leg.start_observation_date,
    end_observation_date: final_leg.end_observation_date,
    instrument_id: strike.underlying_id,
    payoff_barrier_level: knockdown_input_level,
  }
}

export const SupertrackerGenerator: ScheduleGenerator<SupertrackerOptions> = {
  generate: ({ strikes, ...params }) => {
    const legs: ScheduleLeg[] = []

    for (const strike of strikes) {
      const primaryLeg = buildPrimaryLeg({ strike, ...params })

      legs.push(primaryLeg)

      if (params.knockdown_input_level !== null) {
        legs.push(buildBarrierLeg({ strike, final_leg: primaryLeg, ...params }))
      }
    }

    return legs
  },
}
